import { notify } from '@/lib/toast';
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Phone, ShieldCheck, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { usePortalStore } from '@/stores/portal.store';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

export function PortalLoginPage() {
  const navigate = useNavigate();
  const setAuth = usePortalStore((s) => s.setAuth);
  const [step, setStep] = useState<'phone' | 'otp'>('phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [customerName, setCustomerName] = useState('');

  const tenantId = new URLSearchParams(window.location.search).get('tenant') ?? '';

  const requestOtpMutation = useMutation({
    mutationFn: () =>
      axios
        .post('/api/v1/portal/request-otp', { phone }, { headers: { 'x-tenant-id': tenantId } })
        .then((r) => r.data),
    onSuccess: (data) => {
      setCustomerName(data.name ?? '');
      setStep('otp');
      // POS-I109: the server answers the same for every number (it never confirms an account).
      toast.success(data.message ?? 'If this number has an account, a code has been sent by SMS.');
    },
    onError: (err) => notify.error(err, 'Could not request a code. Please try again.'),
  });

  const verifyOtpMutation = useMutation({
    mutationFn: () =>
      axios
        .post('/api/v1/portal/verify-otp', { phone, otp }, { headers: { 'x-tenant-id': tenantId } })
        .then((r) => r.data),
    onSuccess: (data) => {
      setAuth(data.token, data.customer);
      navigate(`/portal/dashboard?tenant=${tenantId}`, { replace: true });
    },
    onError: () => toast.error('Invalid or expired OTP'),
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center p-4">
      <div className="w-full max-w-sm bg-card rounded-2xl shadow-xl p-8">
        <div className="text-center mb-8">
          <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-primary mb-4">
            <ShieldCheck className="h-8 w-8 text-primary-foreground" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">Customer Portal</h1>
          <p className="text-sm text-muted-foreground mt-1">View your invoices, balance & points</p>
        </div>

        {step === 'phone' ? (
          <div className="space-y-4">
            <div>
              <Label className="text-muted-foreground mb-1 block">
                Phone Number
              </Label>
              <div className="relative">
                <Phone className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="tel"
                  dir="ltr"
                  placeholder="+92 300 1234567"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  onKeyDown={(e) =>
                    e.key === 'Enter' && phone.trim() && requestOtpMutation.mutate()
                  }
                  className="ps-10"
                />
              </div>
            </div>
            <Button
              className="w-full"
              onClick={() => requestOtpMutation.mutate()}
              disabled={!phone.trim() || requestOtpMutation.isPending}
            >
              {requestOtpMutation.isPending ? 'Sending…' : 'Get OTP'}
              <ArrowRight className="ms-2 h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {customerName && (
              <p className="text-center text-sm text-muted-foreground">
                Welcome back, <strong>{customerName}</strong>! Enter the OTP sent to {phone}.
              </p>
            )}
            <div>
              <Label className="text-muted-foreground mb-1 block">
                OTP Code
              </Label>
              <Input
                type="number"
                placeholder="123456"
                value={otp}
                onChange={(e) => setOtp(e.target.value.slice(0, 6))}
                onKeyDown={(e) =>
                  e.key === 'Enter' && otp.length === 6 && verifyOtpMutation.mutate()
                }
                className="text-center text-2xl tracking-[0.5em] font-mono"
              />
            </div>
            <Button
              className="w-full"
              onClick={() => verifyOtpMutation.mutate()}
              disabled={otp.length !== 6 || verifyOtpMutation.isPending}
            >
              {verifyOtpMutation.isPending ? 'Verifying…' : 'Verify OTP'}
            </Button>
            <Button
              variant="ghost"
              className="w-full text-sm"
              onClick={() => setStep('phone')}
            >
              Change phone number
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
